import React, {
    useEffect, useRef, useState,
} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import {
    NSCard, NSDropzone, NSFormGroup,
} from 'bricks';
import { ISelectedHTML5File } from 'bricks/types';
import RenderIf from 'components/RenderIf/RenderIf';
import useAwsUploadFiles from 'hooks/useAWSUpload';
import { FEATURES_LIST } from 'services/constants';
import { useAuthContext } from 'contexts/AuthContext';
import useToast from 'hooks/useToast';
import invariant from 'helpers/invariant';
import { AI_ICON_URL, FILE_UPLOAD_CONFIG } from '../constants';
import { ITemporaryUploadedFile } from '../../ProFormaUpload/types';
import FileCard from './FileCard/FileCard';
import './InvestmentMemoDropzone.scss';
import { DocumentLibrarySuggestions } from './DocumentLibrarySuggestions/DocumentLibrarySuggestions';
import { AISuggestedDocument } from '../types';

type Props = {
    label: string;
    multiple?: boolean;
    defaultImageUrl?: string;
    showFileCards?: boolean;
    onUploaded: (file: ITemporaryUploadedFile) => void;
    onDeleted: (file: ITemporaryUploadedFile) => void;
    documentLibrarySuggestions?: {
        suggestions: AISuggestedDocument[];
        isLoadingSuggestions: boolean;
        selectedIds: Set<number>;
        onToggle: (doc: AISuggestedDocument, isSelected: boolean) => void;
    }
    onDefaultImageDeleted?: () => void;
    filesFromData?: ITemporaryUploadedFile[];
    required?: boolean;
    showErrorMsg?: boolean;
    errorMsg?: string | null;
};

const InvestmentMemoDropzone = ({
    label,
    multiple = true,
    defaultImageUrl,
    showFileCards = true,
    onUploaded,
    onDeleted,
    documentLibrarySuggestions,
    onDefaultImageDeleted,
    filesFromData = [],
    required = false,
    showErrorMsg = false,
    errorMsg,
}: Props) => {
    const [pendingFiles, setPendingFiles] = useState<{ id: number; file: ISelectedHTML5File }[]>([]);
    const [isDefaultImageRemoved, setIsDefaultImageRemoved] = useState(false);
    const [imagePreview, setImagePreview] = useState<{ url: string; fileId?: string } | null>(null);
    const uploadedFileIds = useRef<Set<string>>(new Set());
    const uploadedFileRef = useRef<ITemporaryUploadedFile | null>(null);
    const uploadingFilesRef = useRef<Set<string>>(new Set());

    const { selectedOrganizationId } = useAuthContext();
    const { showError } = useToast();

    invariant(selectedOrganizationId, 'Selected organization ID is required');

    const {
        awsUploadedFiles,
        setAwsUploadedFiles,
        uploadFilesWithLimit,
    } = useAwsUploadFiles({
        organizationCorrelationId: selectedOrganizationId,
        feature: FEATURES_LIST.DEAL_DOCUMENTS,
        onError: (message: string) => {
            showError(`Failed to upload file: ${message}`);
        },
    });

    useEffect(() => {
        awsUploadedFiles.forEach(uploaded => {
            if (uploadedFileIds.current.has(uploaded.id.toString())) return;
            uploadedFileIds.current.add(uploaded.id.toString());
            uploadedFileRef.current = uploaded;
            onUploaded(uploaded);
        });

        // Clear uploading tracking for files that have been successfully uploaded
        // This allows users to re-upload files with the same name after deletion
        if (awsUploadedFiles.length === 0 && uploadingFilesRef.current.size > 0) {
            uploadingFilesRef.current.clear();
        }
    }, [awsUploadedFiles, onUploaded]);

    useEffect(() => () => {
        if (imagePreview?.url && imagePreview.fileId) {
            URL.revokeObjectURL(imagePreview.url);
        }
    }, [imagePreview]);

    const handleUploadedFileDelete = (file: ITemporaryUploadedFile | null) => {
        if (!file) return;
        setAwsUploadedFiles(prev => prev.filter(f => f.id !== file.id));
        onDeleted(file);
        if (uploadedFileRef.current?.id === file.id) {
            uploadedFileRef.current = null;
        }
    };

    const handleDeleteDefaultImage = () => {
        setIsDefaultImageRemoved(true);
        onDefaultImageDeleted?.();
    };

    const handleDeletePreview = () => {
        if (imagePreview?.url && imagePreview.fileId) {
            URL.revokeObjectURL(imagePreview.url);
        }
        setImagePreview(null);
        setPendingFiles([]);
        handleUploadedFileDelete(uploadedFileRef.current);
    };

    const hasPreview = imagePreview !== null;
    const showDefaultImage = !showFileCards && defaultImageUrl && !isDefaultImageRemoved && !hasPreview;
    const showImagePreview = !showFileCards && hasPreview;
    const showDropzone = showFileCards || (!showDefaultImage && !showImagePreview);

    const renderImagePreview = (url: string, onDelete: () => void) => (
        <div className="InvestmentMemoDropzone__preview position-relative d-flex align-items-center justify-content-center dzu-dropzone NSDropzone">
            <img src={url} alt="Preview" className="mh-100 mw-100" />
            <FontAwesomeIcon
                icon={faXmark}
                className="position-absolute p-2 cursor--pointer"
                style={{ top: 6, right: 6 }}
                onClick={e => {
                    e.stopPropagation();
                    onDelete();
                }}
            />
        </div>
    );

    return (
        <NSFormGroup>
            <NSCard className="NSCard--level-3 p-3">
                <span className="d-flex align-items-center mb-2 InvestmentMemoDropzone__label">
                    {label}
                    {required && <span className="text-danger"> *</span>}
                    {documentLibrarySuggestions && (
                        <img
                            src={AI_ICON_URL}
                            className="NSInput__label-ai-icon rounded"
                            alt="Northspyre AI"
                        />
                    )}
                </span>
                <div className="d-flex flex-column InvestmentMemoDropzone__files">
                    {documentLibrarySuggestions && (
                        <DocumentLibrarySuggestions
                            suggestions={documentLibrarySuggestions.suggestions}
                            isLoadingSuggestions={documentLibrarySuggestions.isLoadingSuggestions}
                            selectedIds={documentLibrarySuggestions.selectedIds}
                            onToggle={documentLibrarySuggestions.onToggle}
                        />
                    )}
                    <RenderIf isTrue={showFileCards && filesFromData.length > 0}>
                        <div className="d-flex flex-column InvestmentMemoDropzone__uploaded mb-2">
                            {filesFromData.map(file => (
                                <FileCard
                                    key={file.id}
                                    file={file}
                                    onDelete={() => handleUploadedFileDelete(file)}
                                />
                            ))}
                        </div>
                    </RenderIf>

                    {(showDefaultImage || showImagePreview) && renderImagePreview(
                        showImagePreview ? imagePreview.url : defaultImageUrl ?? '',
                        showImagePreview ? handleDeletePreview : handleDeleteDefaultImage,
                    )}

                    {showDropzone && (
                        <NSDropzone
                            accept={FILE_UPLOAD_CONFIG.ACCEPTED_IMAGE_TYPES}
                            maxSizeBytes={FILE_UPLOAD_CONFIG.MAX_FILE_SIZE_BYTES}
                            multiple={multiple}
                            pendingFiles={pendingFiles}
                            setPendingFiles={updater => {
                                setPendingFiles(prev => {
                                    const rawNext = typeof updater === 'function' ? updater(prev) : updater;
                                    const next = multiple ? rawNext : rawNext.slice(-1);
                                    const added = next.filter(nf => !prev.some(pf => pf.id === nf.id));

                                    if (added.length > 0) {
                                        if (multiple) {
                                            const newFilesToUpload = added.filter(addedFile => {
                                                const fileKey = `${addedFile.file.fileObject.name}-${addedFile.file.fileObject.size}`;
                                                return !uploadingFilesRef.current.has(fileKey);
                                            });

                                            if (newFilesToUpload.length === 0) {
                                                return next;
                                            }

                                            newFilesToUpload.forEach(addedFile => {
                                                const fileKey = `${addedFile.file.fileObject.name}-${addedFile.file.fileObject.size}`;
                                                uploadingFilesRef.current.add(fileKey);
                                            });

                                            const filesToUpload = newFilesToUpload.map(addedFile => ({
                                                id: crypto.randomUUID(),
                                                file: addedFile.file.fileObject,
                                            }));

                                            uploadFilesWithLimit(filesToUpload).catch(err => {
                                                console.error('Failed to upload files', err);
                                                showError(`Failed to upload files: ${err}`);
                                            });
                                        } else {
                                            const fileToUpload = added[added.length - 1];
                                            const uploadId = crypto.randomUUID();
                                            const previewUrl = URL.createObjectURL(fileToUpload.file.fileObject);

                                            if (imagePreview?.fileId) {
                                                URL.revokeObjectURL(imagePreview.url);
                                            }
                                            handleUploadedFileDelete(uploadedFileRef.current);
                                            setImagePreview({ url: previewUrl, fileId: uploadId });

                                            uploadFilesWithLimit([{ id: uploadId, file: fileToUpload.file.fileObject }]).catch(err => {
                                                console.error('Failed to upload file', err);
                                                showError(`Failed to upload file: ${err}`);
                                                setImagePreview(prev => {
                                                    if (prev?.fileId === uploadId) {
                                                        URL.revokeObjectURL(previewUrl);
                                                        return null;
                                                    }
                                                    return prev;
                                                });
                                            });
                                        }
                                    }

                                    return next;
                                });
                            }}
                            dragAndDropText="Drag & drop files or"
                            inputContent={`Recommended: PNG, JPG or SVG (max. ${FILE_UPLOAD_CONFIG.MAX_FILE_SIZE_LABEL})`}
                            autoUpload={false}
                        />
                    )}
                </div>
            </NSCard>
            {showErrorMsg && errorMsg && (
                <p className="text-danger small mt-1 mb-0">{errorMsg}</p>
            )}
        </NSFormGroup>
    );
};

export default InvestmentMemoDropzone;
